/*
 * @Author: 许炎 xuyan@3jyx.cn
 * @FilePath: /mall-fe/src/pages/_app.tsx
 * @Description: 商城首页入口文件 服务端预渲染
 *
 */
import type { NextPage } from 'next';
import App from 'next/app';
import Head from 'next/head';
import { useRouter } from 'next/router';
import type { ReactElement, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';

import { getSiteConfig } from 'api/std';
import { getRuntimeConfiguration, getUserDetail } from 'api/user';

import Color from 'color';
import CartContextProvider from 'comps/CartContextProvider';
import CategoryProvider from 'comps/CategoryProvider';
import GeneralErrorBoundary from 'comps/GeneralErrorBoundary';
import { MainLayout } from 'comps/Layout';
import LocContextProvider from 'comps/LocContextProvider';
import MediaQueryProvider from 'comps/MediaQueryProvider';
import NoticeListModal from 'comps/NoticeListModal';
import OrderContextProvider from 'comps/OrderContextProvider';
import ConfigurationProvider from 'comps/ConfigurationProvider';
import RuntimeConfigurationProvider from 'comps/RuntimeConfigurationProvider';
import PricePermissionProvider from 'comps/PricePermissionProvider';
import ProcureEnumProvider from 'comps/ProcureEnumProvider';
import {
  DeviceContext,
  ModalContext,
  NavConfigContext,
  SiteContext,
  UserContext,
} from 'libs/contexts';
import { useSentryContext } from 'libs/sentry';
import { UserSource } from 'libs/enums';
import { webps } from 'libs/imgs';
import { initSdk } from 'libs/qywx';
import { captureError } from 'libs/sentry';
import { useBaiduPv } from 'libs/baiduTongji';
import { updateSiteConfigFromPortalFurnish } from 'libs/std-util';
import { Skip } from 'libs/types';
import { ENV, STATIC_HREFS } from 'libs/urls';
import { isMobileMall } from 'libs/utils';
import { queryClient } from 'libs/react-query';
import { useModalManager } from 'hooks/useModalManager';
import { useDopIntegration } from 'hooks/useDopIntegration';

import theme from 'themes/index';
// eslint-disable-next-line import/no-extraneous-dependencies
import moment from 'moment';

import 'assets/font-icon/iconfont.css';
import 'react-vant/lib/index.css';
import 'moment/locale/zh-cn';

moment.locale('zh-cn');

const isServer = typeof window === 'undefined';

export type NextPageWithLayout<P = {}, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode;
  layoutProps?: any;
};

// 设置全局样式
function getStdThemeStyles(primaryColor) {
  return `
    :root {
      --primaryColor: ${primaryColor};
      --fontPrimaryColor: ${primaryColor};
      --noticeCardTag: ${primaryColor};
      --smsVerTextColor: ${primaryColor};
      --smsVerBackground: ${Color(primaryColor).rotate(5).fade(0.91)};
      --smsVerBorderColor: ${Color(primaryColor).lighten(0.3)};
    }
  `;
}

// 首页导航配置
export function generateNavConfig(config: Partial<SiteConfig>): NavConfigInterface {
  if (!config) {
    return {} as any;
  }
  // 商城首页导航配置类型
  const nav: NavConfigInterface = {
    header: [],
    leftNav: [],
    hotWords: [],
    logo: [],
    miniHeader: [],
    footer: [],
    headerInMobile: [],
  };

  nav.logo.push({
    imageUrl: config.global?.logoUrl,
    roleType: 15,
    showInMobile: false,
    name: 'logo',
    link: '/',
    target: '_top',
    comment: '',
    isExternalUrl: true,
  });

  //  商城首页导航配置信息
  const modules: Nav[] = [
    {
      name: '首页',
      link: '/',
      target: '_top',
      comment: '',
      isExternalUrl: false,
      roleType: 15,
      showInMobile: false,
    },
    config.basic?.enableModuleTender
      ? {
          name: config.global?.tenderItemName,
          link: config.global?.tenderItemUrl ? config.global?.tenderItemUrl : '/tender/hall',
          target: config.global?.tenderItemUrl ? '_blank' : '_top',
          comment: '',
          isExternalUrl: !!config.global?.tenderItemUrl,
          roleType: 15,
          showInMobile: false,
        }
      : null,
    config.basic?.enableModuleGroupBuy
      ? {
          name: config.global?.groupBuyItemName,
          link: config.global?.groupBuyItemUrl ? config.global.groupBuyItemUrl : '/groupbuy',
          target: config.global?.groupBuyItemUrl ? '_blank' : '_top',
          comment: '',
          isExternalUrl: !!config.global?.groupBuyItemUrl,
          roleType: 15,
          showInMobile: false,
        }
      : null,
    config.basic?.enableModuleMall
      ? {
          name: config.global?.mallItemName,
          link: config.global?.mallItemUrl
            ? config.global.mallItemUrl
            : `//${config.basic?.mallDomain}`,
          target: '_blank',
          comment: '',
          isExternalUrl: true,
          roleType: 15,
          showInMobile: false,
        }
      : null,
    config.basic?.enableModuleFinancial
      ? {
          name: config.global?.financeItemName,
          link: config.global?.financeItemUrl ? config.global.financeItemUrl : '/scf',
          target: config.global?.financeItemUrl ? '_blank' : '_top',
          comment: '',
          isExternalUrl: !!config.global?.financeItemUrl,
          roleType: 15,
          showInMobile: false,
        }
      : null,
  ].filter(Boolean);

  nav.header.push(...modules);

  return nav;
}

function buildImgSrc(src: string): string {
  return this.supportsWebp && webps[src] ? webps[src] : src;
}

export default function WebApp({
  Component,
  pageProps,
  userInfo,
  navConfig,
  siteConfig,
  deviceInfo,
  isMobile,
  runtimeConfiguration,
}: {
  Component: NextPageWithLayout;
  pageProps: any;
  userInfo: UserInfo | null;
  navConfig: any;
  siteConfig: any;
  deviceInfo: DeviceContextInterface;
  isMobile: boolean;
  runtimeConfiguration: any;
}) {
  // 用于给页面组件Component套上一层布局组件
  const getLayout = Component.getLayout ?? ((page) => page);
  // 关于布局组件的额外信息
  const { layoutProps } = Component;

  const router = useRouter();

  // 统一管理弹窗相关逻辑
  const { visibleNoticeListModal, setVisibleNoticeListModal, modalContextValue } =
    useModalManager(isMobile);

  // 统一管理 DOP 接入相关逻辑
  useDopIntegration();

  // Sentry 上下文初始化（需要在组件中使用 userInfo）
  useSentryContext(userInfo, siteConfig);

  useBaiduPv();

  // dev、st环境开启测试工具
  useEffect(() => {
    const openToolsdevs = ['zjdev', 'zjst'];
    const curDev = ENV;
    const isNeedDev = isMobile && openToolsdevs.includes(curDev);
    let vConsole;
    let styleEl: HTMLStyleElement | null = null;
    if (isNeedDev) {
      try {
        vConsole = require('vconsole');
        vConsole = new vConsole({ maxLogNumber: 1000 });
        // 上移调试按钮，避免挡住底部 Tab「我的」
        styleEl = document.createElement('style');
        styleEl.setAttribute('data-vc-tabbar-fix', 'true');
        styleEl.textContent =
          '.vc-switch{bottom:calc(70px + env(safe-area-inset-bottom, 0px)) !important;}';
        document.head.appendChild(styleEl);
      } catch (error) {
        console.error(`vconsole 异常:${error}`);
      }
    }
    return () => {
      isNeedDev && vConsole?.destroy();
      styleEl?.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    initSdk();
  }, [router.pathname]);

  const [deviceData, setDeviceData] = useState<DeviceContextInterface>(() => {
    const data = { ...deviceInfo };
    data.buildImgSrc = buildImgSrc.bind(data);
    return data;
  });

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-shadow
    setDeviceData((deviceData) => {
      const data = { ...deviceInfo, supportsWebp: deviceData.supportsWebp };
      data.buildImgSrc = buildImgSrc.bind(data);
      return data;
    });
  }, [deviceInfo]);
  // 加载全局样式配置
  const themeStyles = theme;
  const platformMainColor = siteConfig?.global?.platformMainColor || '#018DD7';
  const stdThemeStyles = getStdThemeStyles(platformMainColor);

  const staticHref = STATIC_HREFS[ENV];

  const antdCSSFile = `${staticHref}/framework/antd4-${platformMainColor?.substring(1)}.css`;

  // NOTE: OSS/OBS 配置设置已移至 RuntimeConfigurationProvider 中处理

  if (router.pathname === '/logistics-map' || router.pathname === '/download-shb') {
    return (
      <>
        <Head>
          <link rel="stylesheet" href={antdCSSFile} />
        </Head>
        <GeneralErrorBoundary>
          <QueryClientProvider client={queryClient}>
            <DeviceContext.Provider value={deviceData}>
              <Component {...pageProps} />
            </DeviceContext.Provider>
          </QueryClientProvider>

          <style jsx global>{`
            ${themeStyles}
            ${stdThemeStyles}

            * {
              padding: 0;
              margin: 0;
              outline: none;
              list-style: none;
              box-sizing: border-box;
            }

            a {
              text-decoration: none;
            }

            html,
            body {
              -webkit-overflow-scrolling: touch;
            }

            .clearfix:before,
            .clearfix:after {
              content: "";
              display: table;
            }

            .clearfix:after {
              clear: both;
            }
            .ellipsis {
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }

            .simple-bottom-transition-enter {
              transform: translateY(100%);
            }

            .simple-bottom-transition-enter-active {
              transform: translateY(0);
              transition: transform 0.3s;
            }

            .simple-bottom-transition-enter-done {
              transform: translateY(0);
            }

            .simple-bottom-transition-exit {
              transform: translateY(0);
            }

            .simple-bottom-transition-exit-active {
              transform: translateY(100%);
              transition: transform 0.3s;
            }

            .simple-bottom-transition-exit-done {
              transform: translateY(100%);
            }

            .simple-right-transition-enter {
              transform: translateX(100%);
            }

            .simple-right-transition-enter-active {
              transform: translateX(0);
              transition: transform 0.3s;
            }

            .simple-right-transition-enter-done {
              transform: translateX(0);
            }

            .simple-right-transition-exit {
              transform: translateX(0);
            }

            .simple-right-transition-exit-active {
              transform: translateX(100%);
              transition: transform 0.3s;
            }

            .simple-right-transition-exit-done {
              transform: translateX(100%);
            }

            /* 定制react-vant 样式 */
            :root {
              --rv-primary-color: ${platformMainColor};
              --rv-field-placeholder-text-color: #bbbbbb;
              --rv-calendar-selected-day-background-color: ${platformMainColor};
              --rv-button-danger-background-color: ${platformMainColor};
              --rv-button-danger-border-color: ${platformMainColor};
              --rv-action-bar-button-danger-color: ${platformMainColor};
              --rv-tabbar-height: 70px;
            }
            /* 定制react-vant dialog button 样式 */
            .rv-dialog__footer.rv-action-bar {
              /*移动端兼容性*/
              gap: 16px;
            }
            .rv-dialog__footer .rv-action-bar-button {
              border-radius: 18px;
            }
            .rv-dialog__footer .rv-dialog__cancel {
              border: 1px solid #e5e5e5;
              color: #333;
              background-color: #fff;
              background-image: none;
            }
            .rv-custom-dialog .rv-custom-children {
              padding: var(--rv-dialog-has-title-message-padding-top)
                var(--rv-padding-lg) var(--rv-padding-md);
              text-align: center;
              font-size: 14px;
              color: var(--rv-text-color);
            }
            .rv-custom-dialog .rv-custom-children .icon {
              margin-right: 6px;
              font-size: 18px;
            }
            .rv-custom-dialog .rv-custom-children .warning {
              color: #f1a024;
            }
          `}</style>
        </GeneralErrorBoundary>
      </>
    );
  }

  return (
    <>
      <Head>
        <link rel="stylesheet" href={antdCSSFile} />
      </Head>

      <GeneralErrorBoundary>
        <QueryClientProvider client={queryClient}>
          {/* 客户端数据 Providers - 使用 React Query 获取数据 */}
          <ConfigurationProvider>
            <RuntimeConfigurationProvider initialData={runtimeConfiguration}>
              <PricePermissionProvider>
                <ProcureEnumProvider>
                  {/* 服务端数据 Providers - 直接提供数据 */}
                  <UserContext.Provider value={userInfo}>
                    <NavConfigContext.Provider value={navConfig}>
                      <ModalContext.Provider value={modalContextValue}>
                        <MediaQueryProvider isMobile={isMobile}>
                          <SiteContext.Provider value={siteConfig}>
                            <LocContextProvider>
                              <CategoryProvider>
                                <CartContextProvider>
                                  <OrderContextProvider>
                                    <DeviceContext.Provider value={deviceData}>
                                      {/* <NProgress> */}
                                      <MainLayout getLayout={getLayout} layoutProps={layoutProps}>
                                        <Component {...pageProps} />
                                      </MainLayout>
                                      <NoticeListModal
                                        visible={visibleNoticeListModal}
                                        setVisible={setVisibleNoticeListModal}
                                      />
                                      {/* </NProgress> */}
                                    </DeviceContext.Provider>
                                  </OrderContextProvider>
                                </CartContextProvider>
                              </CategoryProvider>
                            </LocContextProvider>
                          </SiteContext.Provider>
                        </MediaQueryProvider>
                      </ModalContext.Provider>
                    </NavConfigContext.Provider>
                  </UserContext.Provider>
                </ProcureEnumProvider>
              </PricePermissionProvider>
            </RuntimeConfigurationProvider>
          </ConfigurationProvider>
        </QueryClientProvider>

        <style jsx global>{`
          ${themeStyles}
          ${stdThemeStyles}

          * {
            padding: 0;
            margin: 0;
            outline: none;
            list-style: none;
            box-sizing: border-box;
          }

          a {
            text-decoration: none;
          }

          html,
          body {
            -webkit-overflow-scrolling: touch;
          }

          .clearfix:before,
          .clearfix:after {
            content: "";
            display: table;
          }

          .clearfix:after {
            clear: both;
          }
          .ellipsis {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .simple-bottom-transition-enter {
            transform: translateY(100%);
          }

          .simple-bottom-transition-enter-active {
            transform: translateY(0);
            transition: transform 0.3s;
          }

          .simple-bottom-transition-enter-done {
            transform: translateY(0);
          }

          .simple-bottom-transition-exit {
            transform: translateY(0);
          }

          .simple-bottom-transition-exit-active {
            transform: translateY(100%);
            transition: transform 0.3s;
          }

          .simple-bottom-transition-exit-done {
            transform: translateY(100%);
          }

          .simple-right-transition-enter {
            transform: translateX(100%);
          }

          .simple-right-transition-enter-active {
            transform: translateX(0);
            transition: transform 0.3s;
          }

          .simple-right-transition-enter-done {
            transform: translateX(0);
          }

          .simple-right-transition-exit {
            transform: translateX(0);
          }

          .simple-right-transition-exit-active {
            transform: translateX(100%);
            transition: transform 0.3s;
          }

          .simple-right-transition-exit-done {
            transform: translateX(100%);
          }

          /* 定制react-vant 样式 */
          :root {
            --rv-primary-color: ${platformMainColor};
            --rv-field-placeholder-text-color: #bbbbbb;
            --rv-calendar-selected-day-background-color: ${platformMainColor};
            --rv-button-danger-background-color: ${platformMainColor};
            --rv-button-danger-border-color: ${platformMainColor};
            --rv-action-bar-button-danger-color: ${platformMainColor};
            --rv-tabbar-height: 70px;
          }
          /* 定制react-vant dialog button 样式 */
          .rv-dialog__footer.rv-action-bar {
            /*移动端兼容性*/
            gap: 16px;
          }
          .rv-dialog__footer .rv-action-bar-button {
            border-radius: 18px;
          }
          .rv-dialog__footer .rv-dialog__cancel {
            border: 1px solid #e5e5e5;
            color: #333;
            background-color: #fff;
            background-image: none;
          }
          .rv-custom-dialog .rv-custom-children {
            padding: var(--rv-dialog-has-title-message-padding-top)
              var(--rv-padding-lg) var(--rv-padding-md);
            text-align: center;
            font-size: 14px;
            color: var(--rv-text-color);
          }
          .rv-custom-dialog .rv-custom-children .icon {
            margin-right: 6px;
            font-size: 18px;
          }
          .rv-custom-dialog .rv-custom-children .warning {
            color: #f1a024;
          }
        `}</style>
      </GeneralErrorBoundary>
    </>
  );
}

const handleCaptureError = (e) => {
  if (isServer && !(e.code === 403 || e.code === 401 || e.code === 400101)) {
    captureError(e);
  }
};

WebApp.getInitialProps = async (appContext) => {
  // calls page's `getInitialProps` and fills `appProps.pageProps`
  const appProps = await App.getInitialProps(appContext);
  const skip: Skip = appContext?.Component?.skip;

  const serverSide = !!appContext.ctx.req;
  let serverHeaders = appContext.ctx.req?.headers;
  //   服务端请求设置cookie
  //   const { wxCode: QwxCode, needBind: QneedBind } = appContext?.ctx?.req?.query || {};
  // const { openId, needBind, miniKey } = appContext?.ctx?.req?.query || {};

  /**
   * 服务端数据获取优化
   *
   * 保留在服务端：
   * - siteConfig: 用于生成导航配置和主题颜色（CSS 变量），首屏必需
   * - runtimeConfiguration: updateSiteConfigFromPortalFurnish 依赖此配置
   * - deviceInfo/isMobile: 用于判断移动端/PC端，影响首屏布局
   * - userInfo: 用户信息（保留在服务端获取，详见说明）
   *
   * 迁移到客户端（通过 React Query 和 Provider 获取）：
   * - configuration: 运营配置（通过 ConfigurationProvider）
   * - priceData: 商品价格权限（通过 PricePermissionProvider）
   * - procureEnumDictionary: 采购枚举字典（通过 ProcureEnumProvider）
   *
   * 说明：为什么 userInfo 保留在服务端？
   * 代码审查发现，项目中存在大量依赖 userInfo 的 hooks 和组件，这些代码中很多
   * useEffect 等 hooks 的依赖数组中没有包含 userInfo。如果将 userInfo 迁移到客户端
   * 异步获取，可能会导致这些 hooks 在 userInfo 变化时不会重新执行，存在逻辑错误风险。
   * 为了保持系统稳定性，暂时保留在服务端获取。
   *
   * 性能提升：减少服务端等待时间，预计 TTFB 减少 20-40%
   */
  let runtimeConfiguration = [];
  let navConfig = {};
  let siteConfig: Partial<SiteConfig> = null;
  let userInfo: UserInfo | null = null;

  if (skip?.request !== true) {
    // 只保留首屏渲染必需的数据获取
    await Promise.all([
      // 保留：运行时配置（updateSiteConfigFromPortalFurnish 依赖）
      (async () => {
        try {
          const configRes = await getRuntimeConfiguration({
            serverSide,
            headers: serverHeaders,
          });
          if (configRes.code === 200) {
            runtimeConfiguration = configRes.data || runtimeConfiguration;
          }
        } catch (e) {
          handleCaptureError(e);
        }
      })(),
      // 保留：站点配置（首屏导航和主题必需）
      (async () => {
        try {
          siteConfig = await getSiteConfig({
            serverSide,
            headers: serverHeaders,
          });

          navConfig = generateNavConfig(siteConfig);

          if (serverSide) {
            appContext.ctx.req._tenantCode = siteConfig.tenantCode;
          }
        } catch (e) {
          siteConfig = {};
          navConfig = {};
          handleCaptureError(e);
        }
      })(),
      // 保留：用户信息（保留在服务端获取，原因见上方注释）
      (async () => {
        try {
          const userRes = await getUserDetail({
            serverSide,
            headers: serverHeaders,
            query: { _t: Date.now() },
          });

          if (userRes.code === 200) {
            // 转换数据格式，与 useUserDetail 中的格式保持一致
            const data = userRes.data.profile;
            userInfo = {
              name: data.displayName,
              isPurchaser: data.isPurchaser,
              isSupplier: data.isSupplier,
              isSystemUser: data.isSystemUser,
              permissions: userRes.data.permissions,
              organizationRoleType: data.organizationRoleType,
              mobile: data.mobile,
              avatar: data.avatar,
              status: data.status,
              supplierStatus: data.supplierStatus,
              purchaserStatus: data.purchaserStatus,
              organizationName: data.organizationName,
              organizationCode: data.organizationCode,
              organizationSysNo: data.organizationSysNo,
              supplierSysNo: data.supplierSysNo || null,
              applications: userRes.data?.applications || [],
              completeInfo: userRes.data,
              organizationSource: data.organizationSource,
              isInside: data.organizationSource === UserSource.inside,
              isPersonal: data.isPersonal,
              currentUserType: data?.currentUserType,
              realAuthentication: data?.realAuthentication,
              userAuditType: data.userAuditType,
              orgAuditType: data.orgAuditType,
              sysNo: data.sysNo,
              selfOrgCode: data.selfOrgCode,
              customizeOrgFlag: data.customizeOrgFlag,
            };
          } else {
            // 返回 null 表示未登录
            userInfo = null;
          }
        } catch (e) {
          // 获取用户信息失败，可能是未登录
          userInfo = null;
          handleCaptureError(e);
        }
      })(),
    ]);

    const { preview } = appContext.ctx.query;
    const newSiteConfig = await updateSiteConfigFromPortalFurnish(
      siteConfig,
      runtimeConfiguration,
      {
        serverSide,
        headers: serverHeaders,
        query: {
          preview: !!preview,
        },
      },
    );
    if (newSiteConfig !== siteConfig) {
      siteConfig = newSiteConfig;
      navConfig = generateNavConfig(siteConfig);
    }
  }

  const deviceInfo: any = {
    userAgent:
      typeof navigator === 'undefined'
        ? appContext.ctx?.req?.headers?.['user-agent']
        : navigator.userAgent,
    supportsWebp: serverSide
      ? appContext.ctx?.req?.headers?.accept?.includes('image/webp')
      : document.createElement('canvas').toDataURL('image/webp').indexOf('data:image/webp') === 0,
  };

  return {
    ...appProps,
    // 服务端返回的数据（首屏必需）
    userInfo,
    siteConfig,
    navConfig,
    deviceInfo,
    isMobile: isMobileMall(appContext.ctx?.req),
    // runtimeConfiguration 作为初始数据传递给 RuntimeConfigurationProvider
    // 客户端会使用此数据作为初始值，避免重复请求
    runtimeConfiguration,
  };
};
